import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Wrench, Power, Snowflake, Flame, AlertOctagon, Droplets, Volume2, Thermometer, CircleStop, Fan, TrendingDown, Gauge, FlaskConical, ShieldCheck, Check, AlertTriangle, Clock } from "lucide-react";
import { Reveal, useSeo } from "../lib/motion";
import { Breadcrumb, CTASection } from "../components/CTASection";
import { useSettings, isSet, telHref } from "../context/SettingsContext";

const DEVIS_DEPANNAGE = "/demande-de-devis?univers=climatisation&nature=depannage";

const MOTIFS = [
  { icon: Power, label: "La climatisation ne démarre plus" },
  { icon: Snowflake, label: "Plus de froid" },
  { icon: Flame, label: "Plus de chauffage" },
  { icon: AlertOctagon, label: "Code défaut" },
  { icon: Droplets, label: "Fuite d’eau" },
  { icon: Volume2, label: "Bruit ou vibration anormale" },
  { icon: Thermometer, label: "Température de soufflage anormale" },
  { icon: CircleStop, label: "Unité intérieure à l’arrêt" },
  { icon: Fan, label: "Groupe extérieur à l’arrêt" },
  { icon: TrendingDown, label: "Perte de performance" },
  { icon: Gauge, label: "Défaut de régulation" },
  { icon: FlaskConical, label: "Suspicion de fuite de fluide frigorigène" },
];

const METHODE = [
  { n: "01", t: "Diagnostic" },
  { n: "02", t: "Identification de l’origine de la panne" },
  { n: "03", t: "Explication de la solution au client" },
  { n: "04", t: "Réparation ou proposition de remplacement si nécessaire" },
];

const ENGAGEMENTS = [
  { n: "01", t: "Recherche de la cause", x: "Nous recherchons l’origine du défaut avant de proposer le remplacement d’un équipement." },
  { n: "02", t: "Transparence", x: "Si une réparation, une pièce ou une intervention complémentaire est nécessaire, le client est informé avant engagement des travaux." },
  { n: "03", t: "Réparation lorsqu’elle est pertinente", x: "Lorsque la réparation reste techniquement et économiquement cohérente, elle est privilégiée." },
  { n: "04", t: "Pas de remplacement systématique", x: "Un remplacement complet n’est proposé que lorsque l’état, l’âge, la disponibilité des pièces ou le coût de réparation le justifie." },
  { n: "05", t: "Suivi du dossier", x: "Lorsqu’une pièce doit être commandée ou qu’une seconde intervention est nécessaire, le dossier reste suivi." },
];

const PREPARER = [
  "Marque de l’équipement",
  "Référence",
  "Nombre d’unités",
  "Code défaut",
  "Symptômes constatés",
  "Âge approximatif",
  "Photos",
  "Historique récent de la panne",
];

function Section({ tint = "white", children, testid }) {
  return (
    <section data-testid={testid} className={tint === "cloud" ? "bg-cloud" : "bg-white"}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">{children}</div>
    </section>
  );
}

function Head({ kicker, title, sub, red }) {
  return (
    <Reveal className="mb-8 max-w-3xl">
      <span className={`text-xs font-bold uppercase tracking-[0.25em] ${red ? "text-red-500" : "text-ice"}`}>{kicker}</span>
      <h2 className="font-heading font-black tracking-tight text-3xl md:text-4xl text-ink mt-4 leading-[1.05]">{title}</h2>
      {sub && <p className="text-maintext text-lg mt-4 leading-relaxed">{sub}</p>}
    </Reveal>
  );
}

export default function Depannage() {
  useSeo("Dépannage climatisation", "Votre climatisation est en panne ? HP Froid diagnostique les pannes de climatisation en Alsace et recherche leur origine avant de proposer la réparation adaptée.");
  const { settings } = useSettings();

  return (
    <div className="pt-32 bg-white">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12 md:pb-16" data-testid="depannage-hero">
        <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Dépannage" }]} />
        <Reveal className="mt-8 max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-red-500"><Wrench size={15} /> Dépannage climatisation</span>
          <h1 className="font-heading font-black tracking-tight text-4xl md:text-6xl text-ink mt-5 leading-[0.98]">Votre climatisation est en panne ?</h1>
          <p className="text-maintext text-lg mt-6 leading-relaxed">HP Froid diagnostique les pannes de climatisation et recherche leur origine avant de proposer la réparation adaptée.</p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <Link to={DEVIS_DEPANNAGE} data-testid="depannage-cta" className="inline-flex items-center gap-2 bg-red-500 text-white rounded-md px-8 py-4 font-bold hover:bg-red-600 transition-colors">
              Demander un dépannage <ArrowUpRight size={18} />
            </Link>
            {isSet(settings.phone) && (
              <a href={telHref(settings.phone)} data-testid="depannage-phone" className="inline-flex items-center gap-2 border border-ink text-ink rounded-md px-8 py-4 font-bold hover:border-ice hover:text-ice transition-colors">
                <Phone size={18} /> Nous appeler
              </a>
            )}
          </div>
          {isSet(settings.phone) && (
            <p className="font-heading font-bold text-ink text-lg mt-4">
              {settings.phone}
              {isSet(settings.phone2) && <span className="block mt-1">{settings.phone2}</span>}
            </p>
          )}
        </Reveal>
      </section>

      {/* MOTIFS */}
      <Section tint="cloud" testid="depannage-motifs">
        <Head red kicker="Symptômes" title="Dans quels cas nous contacter ?" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {MOTIFS.map((m, i) => (
            <Reveal key={m.label} delay={i * 0.03}>
              <div className="flex items-center gap-4 bg-white border border-line rounded-md p-5 h-full hover:border-red-300 transition-colors">
                <m.icon size={22} className="text-red-500 shrink-0" />
                <p className="font-semibold text-ink text-[15px] leading-snug">{m.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* MÉTHODE */}
      <Section testid="depannage-methode">
        <Head kicker="Notre méthode" title="Diagnostiquer avant de remplacer" sub="Une panne ne signifie pas automatiquement que l’installation doit être remplacée. Notre première étape consiste à rechercher la cause du dysfonctionnement." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {METHODE.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.07}>
              <div className="border-t-2 border-ice pt-5 h-full">
                <span className="font-heading font-black text-3xl text-ice/25 leading-none">{s.n}</span>
                <p className="font-heading font-bold text-ink mt-3 leading-snug">{s.t}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ENGAGEMENT */}
      <Section tint="cloud" testid="depannage-engagement">
        <Head kicker="Engagement" title="Notre engagement en dépannage" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ENGAGEMENTS.map((e, i) => (
            <Reveal key={e.n} delay={i * 0.05}>
              <div className="bg-white border border-line rounded-md p-6 h-full">
                <span className="font-heading font-black text-2xl text-ice/25 leading-none">{e.n}</span>
                <p className="flex items-center gap-2 font-heading font-bold text-ink mt-3 uppercase tracking-wide text-sm"><ShieldCheck size={16} className="text-ice shrink-0" /> {e.t}</p>
                <p className="text-maintext text-[15px] mt-3 leading-relaxed">{e.x}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CADRE */}
      <Section testid="depannage-cadre">
        <div className="max-w-3xl">
          <Reveal>
            <div className="border-l-4 border-amber-500 bg-amber-50 rounded-r-md p-6">
              <p className="flex items-center gap-2 font-heading font-bold text-ink text-sm uppercase tracking-widest"><AlertTriangle size={16} className="text-amber-600" /> À savoir avant l’intervention</p>
              <p className="text-maintext text-[15px] mt-3 leading-relaxed">Le diagnostic permet d’identifier la panne. Une réparation peut nécessiter une pièce détachée, une recherche complémentaire, une intervention frigorifique ou le remplacement d’un composant. Ces prestations complémentaires sont facturées séparément lorsqu’elles ne sont pas comprises dans le diagnostic initial.</p>
            </div>
          </Reveal>
          <Reveal className="mt-5">
            <p className="flex items-start gap-2.5 text-muted2 text-sm leading-relaxed"><Clock size={15} className="text-ice mt-0.5 shrink-0" /> Les demandes sont traitées dans les meilleurs délais selon les disponibilités, la localisation, le type d’installation et la nature de la panne.</p>
          </Reveal>
        </div>
      </Section>

      {/* PRÉPARER */}
      <Section tint="cloud" testid="depannage-preparer">
        <Head kicker="Avant votre demande" title="Pour gagner du temps lors du diagnostic" sub="Lorsque c’est possible, préparez ces informations :" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3.5 max-w-5xl">
          {PREPARER.map((p) => (
            <p key={p} className="flex items-start gap-2.5 text-maintext text-[15px] leading-relaxed"><Check size={17} className="text-ice mt-0.5 shrink-0" /> {p}</p>
          ))}
        </div>
        <Reveal className="mt-8">
          <Link to={DEVIS_DEPANNAGE} data-testid="depannage-cta-bas" className="inline-flex items-center gap-2 bg-red-500 text-white rounded-md px-8 py-4 font-bold hover:bg-red-600 transition-colors">
            Transmettre ma demande de dépannage <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </Section>

      <CTASection title="Une panne à faire diagnostiquer ?" text="Décrivez le symptôme et votre équipement : nous recherchons la cause avant toute proposition de réparation ou de remplacement." />
    </div>
  );
}
